import { Box, Typography, useTheme } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";

// ─── Mini SVG Chart ───────────────────────────────────────────────────────────
const MiniChart = ({ data = [], gradientId }) => {
  if (!data || data.length < 2) {
    return <Box sx={{ height: 185, backgroundColor: "background.default" }} />;
  }

  const values = data.map((d) => Number(d.value));
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;

  const points = data.map((d, i) => {
    const x = (i / (data.length - 1)) * 100;
    const y = 8 + (1 - (Number(d.value) - min) / range) * 88;
    return `${x},${y}`;
  });

  const pathD = `M ${points.join(" L ")}`;
  const areaD = `${pathD} L 100,100 L 0,100 Z`;

  return (
    <svg
      viewBox="0 0 100 100"
      style={{ width: "100%", height: 185, display: "block" }}
      preserveAspectRatio="none"
    >
      <defs>
        <linearGradient id={gradientId} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#06b6d4" stopOpacity="0.4" />
          <stop offset="100%" stopColor="#06b6d4" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={areaD} fill={`url(#${gradientId})`} />
      <path
        d={pathD}
        fill="none"
        stroke="#06b6d4"
        strokeWidth="2"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
};

// ─── Strategy Card ────────────────────────────────────────────────────────────
const StrategyCard = ({
  strategyID,
  title,
  chartData = [],
  loading = false,
  sharpeRatio,
  periodReturn,
  owner,
  tags = [],
  categories = [],
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const isPositive = (periodReturn ?? 0) >= 0;
  const gradientId = `grad-${String(strategyID).replace(/[^a-z0-9]/gi, "")}`;

  const handleCardClick = () => {
    navigate(`/store/strategy/${strategyID}`, {
      state: { from: location.pathname + location.search },
    });
  };

  return (
    <Box
      onClick={handleCardClick}
      sx={{
        display: "flex",
        flexDirection: "column",
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        cursor: "pointer",
        transition: "border-color 0.2s, box-shadow 0.2s",
        height: "100%",
        minHeight: 30,
        filter: loading ? "blur(3px)" : "none",
        "&:hover": {
          borderColor: theme.palette.primary.main,
          boxShadow: `0 0 0 1px ${theme.palette.primary.main}22`,
        },
      }}
    >
      {/* Chart */}
      <Box sx={{ backgroundColor: theme.palette.background.default, height: 185, overflow: "hidden" }}>
        <MiniChart data={chartData} gradientId={gradientId} />
      </Box>

      {/* Content */}
      <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1, flexGrow: 1 }}>
        {/* Name + owner */}
        <Box>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              color: "text.primary",
              lineHeight: 1.3,
              mb: 0.25,
            }}
          >
            {title}
          </Typography>
          {owner?.name && (
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {owner.name}
            </Typography>
          )}
        </Box>

        {/* Return + Sharpe */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", pt: 0.5 }}>
          <Box>
            <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
              Period Return
            </Typography>
            <Typography
              sx={{
                fontWeight: 700,
                lineHeight: 1.2,
                color: isPositive ? "#22c55e" : "#ef4444",
              }}
            >
              {isPositive ? "+" : ""}
              {periodReturn?.toFixed(2) ?? "0.00"}%
            </Typography>
          </Box>
          <Box sx={{ textAlign: "right" }}>
            <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
              Sharpe Ratio
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500, color: "text.primary" }}>
              {sharpeRatio?.toFixed(2) ?? "N/A"}
            </Typography>
          </Box>
        </Box>

        {/* Categories */}
        {categories.length > 0 && (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
            {categories.slice(0, 3).map((cat) => (
              <Box
                key={cat}
                sx={{
                  px: 1,
                  py: 0.25,
                  borderRadius: 0.75,
                  border: "1px solid rgba(6, 182, 212, 0.5)",
                  backgroundColor: "rgba(6, 182, 212, 0.08)",
                  color: "#06b6d4",
                  fontSize: "0.7rem",
                  fontWeight: 500,
                  whiteSpace: "nowrap",
                }}
              >
                {cat}
              </Box>
            ))}
          </Box>
        )}

        {/* Tags */}
        {tags.length > 0 && (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
            {tags.slice(0, 3).map((tag) => (
              <Box
                key={tag}
                sx={{
                  px: 1,
                  py: 0.25,
                  borderRadius: 0.75,
                  border: "1px solid rgba(168, 85, 247, 0.5)",
                  backgroundColor: "rgba(168, 85, 247, 0.08)",
                  color: "#a855f7",
                  fontSize: "0.7rem",
                  fontWeight: 500,
                  whiteSpace: "nowrap",
                }}
              >
                {tag}
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default StrategyCard;

